import { useState } from "react";
import { toast } from "sonner";
import { Clock, Send } from "lucide-react";
import PublicLayout, { ScamNotice } from "@/components/PublicLayout";
import { Field, BigButton, inputCls, textareaCls } from "@/components/FormBits";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useLang } from "@/lib/i18n";

function StatusResult({ res, creds, onReset }) {
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const send = async () => {
    try { await api.post("/public/status/update", { ...creds, message: msg }); setSent(true); toast.success("Your information was sent securely."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="status-result" className="fade-up rounded-2xl bg-white border border-[#E7DFD5] p-6 sm:p-8 space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#605349]">Reference</p>
        <p className="font-mono text-xl font-bold text-[#4A2E1A]">{res.reference}</p>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#605349]">Current status</p>
        <p data-testid="status-label" className="mt-1 text-2xl font-serif-head font-bold text-[#3D6346]">{res.status_label}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 text-[15px]">
        <p><span className="text-[#605349]">Submitted:</span> {fmtDateTime(res.submitted_at)}</p>
        <p className="flex items-center gap-1"><Clock className="w-4 h-4 text-[#605349]" /><span className="text-[#605349]">Last update:</span> {fmtDateTime(res.last_updated)}</p>
      </div>
      {res.message && <p data-testid="status-message" className="rounded-xl bg-[#FDF7E7] border border-[#E9D49A] p-4">{res.message}</p>}
      {res.can_send_update && !sent && (
        <div className="space-y-3">
          <Field label="Send the additional information requested" htmlFor="upd">
            <textarea id="upd" data-testid="input-requester-update" maxLength={1500} className={textareaCls} value={msg} onChange={(e) => setMsg(e.target.value)} />
          </Field>
          <BigButton type="button" variant="green" data-testid="send-update-button" disabled={!msg.trim()} onClick={send}><Send className="w-5 h-5" /> Send securely</BigButton>
        </div>
      )}
      <p className="text-sm text-[#605349]">For your privacy, only limited information is shown here. For details, please wait for the parish to contact you or visit the parish office.</p>
      <BigButton type="button" variant="ghost" data-testid="check-another-button" onClick={onReset}>Check another request</BigButton>
    </div>
  );
}

export default function CheckStatus() {
  const { t } = useLang();
  const [reference, setRef] = useState("");
  const [pin, setPin] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/public/status", { reference, pin }); setRes(data); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const reset = () => { setRes(null); setRef(""); setPin(""); };
  return (
    <PublicLayout>
      <div className="max-w-xl mx-auto px-5 py-10 sm:py-14">
        <h1 data-testid="check-page-title" className="text-3xl sm:text-4xl font-black">{t("check_title")}</h1>
        <p className="mt-3 mb-8 text-[#605349]">Enter the reference number and the private PIN you received when you submitted your request.</p>
        {res ? <StatusResult res={res} creds={{ reference, pin }} onReset={reset} /> : (
          <form onSubmit={check} className="space-y-5" data-testid="check-status-form">
            <Field label={t("reference")} htmlFor="ref"><input id="ref" data-testid="input-reference-number" className={`${inputCls} font-mono uppercase`} placeholder="MBC-2026-000001" value={reference} onChange={(e) => setRef(e.target.value)} autoComplete="off" /></Field>
            <Field label={t("pin")} htmlFor="pin"><input id="pin" data-testid="input-pin" type="password" inputMode="numeric" maxLength={6} className={`${inputCls} font-mono tracking-[0.3em]`} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} autoComplete="off" /></Field>
            <BigButton type="submit" data-testid="check-status-button" disabled={busy || !reference || pin.length !== 6} className="w-full">{busy ? "Checking…" : t("check_btn")}</BigButton>
            <p className="text-sm text-[#605349]">Lost your PIN? Please visit or call the parish office; staff will verify your identity in person.</p>
            <ScamNotice compact />
          </form>
        )}
      </div>
    </PublicLayout>
  );
}
